import { useCallback, useEffect, useRef, useState } from 'react';
import { AdminInviteError, listSetupInvitations } from '../../../services/adminInvites';
import type { SetupInvitation } from '../../../types/adminInvites';

export interface SetupInvitations {
  /**
   * Null until the first read answers, false when it answered 404 (an older build with no unified
   * invite). Show the Invite user button and the pending panel only when this is true, so an older
   * API never flashes them.
   */
  supported: boolean | null;
  list: SetupInvitation[];
  loading: boolean;
  /** Set when the list failed for any reason other than being unsupported. */
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Open setup invitations for Admin > Organizations, and the feature probe that decides whether the
 * unified invite shows at all.
 *
 * A request counter keeps a slower, older read from overwriting a newer one: the dialog and the
 * panel's own actions both call `reload`.
 */
export const useSetupInvitations = (): SetupInvitations => {
  const [supported, setSupported] = useState<boolean | null>(null);
  const [list, setList] = useState<SetupInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++seqRef.current;

    try {
      const rows = await listSetupInvitations();
      if (seqRef.current !== seq) return;
      setList(rows);
      setSupported(true);
      setError(null);
    } catch (err: unknown) {
      if (seqRef.current !== seq) return;
      if (err instanceof AdminInviteError && err.kind === 'unsupported') {
        setSupported(false);
      } else {
        // Any other failure means the route exists, so the panel shows and carries the error.
        setSupported((s) => s ?? true);
        setError('Pending setup invitations could not be loaded.');
      }
    } finally {
      if (seqRef.current === seq) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    return () => {
      // Nothing is waiting for an in-flight read after unmount.
      seqRef.current += 1;
    };
  }, [reload]);

  return { supported, list, loading, error, reload };
};
